/**
 * Bureau de l'enseignant : un fil conducteur, de la liste de classe à la
 * proclamation.
 *
 * L'écran est organisé en **étapes numérotées**. La barre du haut dit, d'un
 * regard, ce qui est fait, ce qui reste et ce qui coince ; chaque étape ouvre
 * son panneau, et un pas bloqué indique toujours l'étape qui le débloque.
 * Dans chaque panneau, le constat et les actions sont séparés : on lit
 * d'abord, on agit ensuite.
 *
 * Contient l'unique pouvoir discrétionnaire du système — la **décision
 * souveraine** (§6.8) : valider le résultat calculé, ou le modifier. Dans les
 * deux cas, l'acte est motivé, journalisé, et le résultat calculé reste
 * affiché à côté du résultat retenu.
 */

import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import {
  NUL_REASON_LABEL,
  PHASE_LABEL,
  assignLabels,
  parseRoster,
  personLabel,
  randomId,
  retainedSeats,
  type Person,
  type Phase,
  type RosterDetection,
  type Seat,
} from '../../core/index.js';
import { lirePresence } from '../../transport/distant.js';
import { StoreElement } from '../base.js';
import { dateHeure, dateLongue, nomFichier, pluriel, pourcentage, telecharger } from '../format.js';
import { icone } from '../icons.js';
import { ouvrirImpression } from '../impression.js';
import { valeurDe } from '../md.js';
import { etapeRecommandee, etapesParcours, type EtapeParcours } from '../parcours.js';
import { qrSvg, qrSvgTexte } from '../qr.js';

const ROLE_LABEL: Record<string, string> = { titulaire: 'Titulaire', suppleant: 'Suppléant' };

/** Cadence d'interrogation de la présence : celle de l'état public. */
const CADENCE_PRESENCE_MS = 15_000;

type OrdreColonnes = 'auto' | 'prenom_nom' | 'nom_prenom';

@customElement('view-bureau')
export class ViewBureau extends StoreElement {
  @state() private codeEnseignant = '';
  @state() private erreurVerrou = '';
  @state() private codeEnseignantVisible = false;
  @state() private codePerduVisible = false;
  @state() private rosterText = '';
  @state() private rosterOrder: OrdreColonnes = 'auto';
  @state() private rosterApercu: { prenom: string; nom: string }[] = [];
  @state() private rosterDetection: RosterDetection | null = null;
  @state() private rosterAvertissements: readonly string[] = [];
  @state() private editionRoster = false;
  @state() private editionLignes: { id: string; prenom: string; nom: string }[] = [];
  @state() private messageRoster = '';
  @state() private nombreCodes = 0;
  @state() private titulaireId = '';
  @state() private suppleantId = '';
  @state() private motif = '';
  @state() private departageChoisi = '';
  @state() private designeId = '';
  @state() private souverainOuvert = false;
  @state() private forcer: Record<string, string> = {};
  @state() private confirme = false;
  @state() private message = '';
  @state() private messageLiaison = '';
  @state() private publication = false;
  @state() private etapeChoisie: number | null = null;
  @state() private connectes: number | null = null;
  private minuteurPresence: ReturnType<typeof setInterval> | null = null;
  private fichierSauvegarde: HTMLInputElement | null = null;

  private readonly refFichier = (element?: Element): void => {
    this.fichierSauvegarde = (element as HTMLInputElement | undefined) ?? null;
  };

  // ---------------------------------------------------- fil conducteur

  override connectedCallback(): void {
    super.connectedCallback();
    void this.rafraichirPresence();
    this.minuteurPresence = setInterval(() => void this.rafraichirPresence(), CADENCE_PRESENCE_MS);
  }

  override disconnectedCallback(): void {
    if (this.minuteurPresence !== null) clearInterval(this.minuteurPresence);
    this.minuteurPresence = null;
    super.disconnectedCallback();
  }

  /** Nombre d'appareils sur la page de vote, ou `null` si le relais l'ignore. */
  private async rafraichirPresence(): Promise<void> {
    const electionId = this.store.electionId;
    if (electionId === null || !this.store.estEnLigne) {
      this.connectes = null;
      return;
    }
    this.connectes = await lirePresence(electionId);
  }

  private etapes(): readonly EtapeParcours[] {
    const diagnostic = this.store.diagnostic();
    return etapesParcours(this.store.state, this.store.computation, {
      eleves: diagnostic.eleves,
      codes: diagnostic.codes,
      bulletins: diagnostic.bulletins,
      derniereSauvegarde: diagnostic.derniereSauvegarde,
      enLigne: this.store.estEnLigne,
    });
  }

  private get etapeActive(): number {
    return this.etapeChoisie ?? etapeRecommandee(this.etapes());
  }

  /**
   * Colonne de navigation des étapes, à gauche du contenu.
   *
   * Material Web 2.x ne fournit pas de *navigation rail* (seuls `navigationbar`
   * et `navigationdrawer` existent en labs) : on suit donc la spécification M3
   * à la main — pastille d'état, indicateur de sélection en forme de pilule,
   * et `md-ripple` pour la couche d'état au survol et au clic.
   */
  private renderParcours(etapes: readonly EtapeParcours[]) {
    const active = this.etapeActive;
    return html`
      <nav class="parcours" aria-label="Étapes de l’élection">
        ${etapes.map((etape) => {
          const marque =
            etape.etat === 'fait'
              ? icone('check', 18)
              : etape.etat === 'probleme'
                ? icone('alerte', 18)
                : etape.etat === 'bloque'
                  ? icone('verrou', 16)
                  : String(etape.cle);
          return html`
            <button
              type="button"
              class="parcours__etape parcours__etape--${etape.etat}"
              data-etape=${etape.cle}
              aria-current=${etape.cle === active ? 'true' : 'false'}
              title=${`${etape.cle}. ${etape.titre} — ${etape.blocage ?? etape.resume}`}
              @click=${() => (this.etapeChoisie = etape.cle)}
            >
              <md-ripple></md-ripple>
              <span class="parcours__puce" aria-hidden="true">${marque}</span>
              <span class="parcours__texte">
                <span class="parcours__nom">${etape.cle}. ${etape.titre}</span>
                <span class="parcours__etat">${etape.blocage ?? etape.resume}</span>
              </span>
            </button>
          `;
        })}
      </nav>
    `;
  }

  /**
   * Rappel de sauvegarde, visible **dès le début** : c'est le seul moyen de
   * reprendre l'élection sur un autre ordinateur, et il se prépare avant le
   * jour J, pas pendant.
   */
  private renderRappelSauvegarde() {
    const diagnostic = this.store.diagnostic();
    if (diagnostic.derniereSauvegarde !== null) return nothing;
    if (diagnostic.eleves === 0 && diagnostic.codes === 0) return nothing;
    return html`
      <section class="card card--warn no-print">
        <div class="card__head">
          <span class="chip-icon chip-icon--alerte" aria-hidden="true">${icone('alerte', 22)}</span>
          <div class="stack stack--tight grow">
            <h2 class="t-title">Sauvegardez dès maintenant</h2>
            <p class="t-body">
              Le fichier de sauvegarde contient la liste, les codes et le journal. C’est <strong>le seul moyen</strong>
              de reprendre l’élection sur un autre ordinateur, ou après un effacement des données du navigateur.
            </p>
          </div>
        </div>
        <div class="bloc bloc--actions">
          <div class="row">
            <md-filled-tonal-button @click=${() => this.exporterEtOuvrir()}>Exporter une sauvegarde</md-filled-tonal-button>
            <md-text-button @click=${() => (this.etapeChoisie = 6)}>Voir l’étape 6</md-text-button>
          </div>
        </div>
      </section>
    `;
  }

  private exporterEtOuvrir(): void {
    this.sauvegarder();
    this.etapeChoisie = 6;
  }

  /**
   * Supprime l'élection sur cet appareil, après confirmation explicite.
   *
   * C'est l'action destructrice de l'écran : elle est donc isolée derrière une
   * icône dédiée, jamais mélangée aux actions de préparation.
   */
  private async supprimerSession(): Promise<void> {
    const nom = this.store.state.config?.nom ?? 'cette élection';
    const nombre =
      this.store.state.ballots.length > 0
        ? `\n\n${pluriel(this.store.state.ballots.length, 'bulletin déposé sera perdu', 'bulletins déposés seront perdus')}.`
        : '';
    if (
      !confirm(
        `Supprimer définitivement « ${nom} » sur cet appareil ?\n\nLe journal, la planche de codes et la sauvegarde locale seront effacés.${nombre}\n\nLa copie publiée sur le relais suit sa propre expiration (7 jours).`,
      )
    ) {
      return;
    }
    await this.store.purge();
    location.hash = '#/';
  }

  /**
   * Proclamation : un jalon, affiché **hors des panneaux** pour rester visible
   * quel que soit l'onglet ouvert — c'est ce qui a été accompli.
   */
  private renderProclamation() {
    if (this.store.state.phase !== 'PROCLAMATION') return nothing;
    return html`
      <section class="card card--ok">
        <div class="card__head">
          <span class="chip-icon chip-icon--ok" aria-hidden="true">${icone('checkCercle', 24)}</span>
          <div class="stack stack--tight grow">
            <h2 class="t-title">Résultats proclamés</h2>
            <p class="t-body">
              Le procès-verbal s’imprime et s’exporte depuis l’étape 6. Exportez aussi une sauvegarde : c’est le seul
              moyen de reprendre l’élection sur un autre ordinateur.
            </p>
          </div>
        </div>
      </section>
    `;
  }

  // ------------------------------------------------------------ verrouillage

  private deverrouiller(): void {
    if (this.store.unlock(this.codeEnseignant)) {
      this.erreurVerrou = '';
      this.codeEnseignant = '';
    } else {
      this.erreurVerrou = 'Code enseignant incorrect.';
    }
  }

  private renderVerrou() {
    const codes = this.store.codes();
    return html`
      <div class="page page--narrow">
        <section class="card">
          <div class="card__head">
            <span class="chip-icon" aria-hidden="true">${icone('verrou', 22)}</span>
            <h2 class="t-title">Espace enseignant verrouillé</h2>
          </div>
          <p class="t-body t-muted">
            Saisissez le code enseignant, imprimé en tête de la planche de codes. Il est distinct des codes élèves.
          </p>
          <input
            class="code-input"
            type="text"
            autocomplete="off"
            spellcheck="false"
            aria-label="Code enseignant"
            placeholder="XXXXX-XXXXX-XXX"
            .value=${this.codeEnseignant}
            @input=${(evenement: Event) => {
              this.codeEnseignant = valeurDe(evenement);
              this.erreurVerrou = '';
            }}
            @keydown=${(evenement: KeyboardEvent) => {
              if (evenement.key === 'Enter') this.deverrouiller();
            }}
          />
          ${this.erreurVerrou ? html`<p class="t-body t-error">${this.erreurVerrou}</p>` : nothing}
          <div class="row row--between">
            <md-text-button @click=${() => (location.hash = '#/')}>Accueil</md-text-button>
            <md-filled-button ?disabled=${this.codeEnseignant.trim().length === 0} @click=${() => this.deverrouiller()}
              >Déverrouiller</md-filled-button
            >
          </div>
        </section>

        <section class="card card--flat no-print">
          <p class="t-label">Code perdu ?</p>
          <p class="t-body t-muted">
            Les codes de cette élection sont conservés sur cet appareil, puisqu’ils y ont été générés.
            ${codes
              ? html`<br /><strong>À n’utiliser que si vous êtes bien l’enseignant :</strong>
                  ${this.codePerduVisible
                    ? html`<span class="t-mono">${codes.enseignant.display}</span>`
                    : html`<span class="masque" aria-label="Code enseignant masqué">•••••-•••••-•••</span>`}
                  <md-text-button @click=${() => (this.codePerduVisible = !this.codePerduVisible)}
                    >${this.codePerduVisible ? 'Masquer' : 'Afficher'}</md-text-button
                  >`
              : html`<br />Aucun code n’est enregistré sur cet appareil pour cette élection.`}
          </p>
          <p class="t-body t-muted">
            <strong>Ne projetez jamais cet écran&nbsp;:</strong> le code enseignant ouvre toutes les décisions
            souveraines du scrutin.
          </p>
        </section>
      </div>
    `;
  }

  // ------------------------------------------------------------- liste classe

  private analyserRoster(): void {
    if (this.rosterText.trim().length === 0) {
      this.rosterApercu = [];
      this.rosterDetection = null;
      this.rosterAvertissements = [];
      return;
    }
    const analyse = parseRoster(this.rosterText, { order: this.rosterOrder });
    this.rosterApercu = analyse.persons.map((personne) => ({ prenom: personne.prenom, nom: personne.nom }));
    this.rosterDetection = analyse.detection;
    this.rosterAvertissements = analyse.warnings;
  }

  private majLigne(index: number, champ: 'prenom' | 'nom', valeur: string): void {
    this.rosterApercu = this.rosterApercu.map((ligne, rang) =>
      rang === index ? { ...ligne, [champ]: valeur } : ligne,
    );
  }

  private supprimerLigne(index: number): void {
    this.rosterApercu = this.rosterApercu.filter((_, rang) => rang !== index);
  }

  private ajouterLigne(): void {
    this.rosterApercu = [...this.rosterApercu, { prenom: '', nom: '' }];
  }

  // ----------------------------------------- correction de la liste importée

  private commencerEditionRoster(): void {
    this.editionLignes = this.store.state.persons.map((personne) => ({
      id: personne.id,
      prenom: personne.prenom,
      nom: personne.nom,
    }));
    this.editionRoster = true;
    this.messageRoster = '';
  }

  private annulerEditionRoster(): void {
    this.editionRoster = false;
    this.editionLignes = [];
    this.messageRoster = '';
  }

  private majEdition(index: number, champ: 'prenom' | 'nom', valeur: string): void {
    this.editionLignes = this.editionLignes.map((ligne, rang) =>
      rang === index ? { ...ligne, [champ]: valeur } : ligne,
    );
  }

  private retirerEdition(index: number): void {
    const ligne = this.editionLignes[index];
    if (ligne === undefined) return;
    /*
      Le refus est prononcé **au clic**, pas à l'enregistrement : retirer la
      ligne du formulaire pour la refuser ensuite ferait perdre les autres
      corrections en cours.
    */
    const declaree = this.store.state.declarations.some(
      (declaration) => declaration.titulaireId === ligne.id || declaration.suppleantId === ligne.id,
    );
    if (declaree) {
      this.messageRoster =
        'Cette personne est déclarée candidate : supprimez d’abord sa candidature à l’étape 3 — Candidatures.';
      return;
    }
    this.messageRoster = '';
    this.editionLignes = this.editionLignes.filter((_, rang) => rang !== index);
  }

  private ajouterEdition(): void {
    this.editionLignes = [...this.editionLignes, { id: randomId('p'), prenom: '', nom: '' }];
  }

  /**
   * Enregistre la liste corrigée **en conservant les identifiants** : corriger
   * une faute ne doit pas décaler les `p_N` et fausser les candidatures déjà
   * déclarées. Une personne déclarée ne peut pas être retirée : on renvoie à
   * l'étape 3 plutôt que de laisser une candidature orpheline.
   */
  private async enregistrerEditionRoster(): Promise<void> {
    const retenues = this.editionLignes.filter(
      (ligne) => ligne.prenom.trim() !== '' || ligne.nom.trim() !== '',
    );
    if (retenues.length === 0) {
      this.messageRoster = 'La liste ne peut pas être vide. Pour tout effacer, utilisez la corbeille.';
      return;
    }
    const idsRetenus = new Set(retenues.map((ligne) => ligne.id));
    const orphelines = this.store.state.declarations.filter(
      (declaration) =>
        !idsRetenus.has(declaration.titulaireId) ||
        (declaration.suppleantId !== undefined && !idsRetenus.has(declaration.suppleantId)),
    );
    if (orphelines.length > 0) {
      this.messageRoster =
        'Une personne déclarée candidate ne peut pas être retirée : supprimez d’abord sa candidature à l’étape 3 — Candidatures.';
      return;
    }
    await this.store.importRoster(assignLabels(retenues, retenues.map((ligne) => ligne.id)));
    this.editionRoster = false;
    this.editionLignes = [];
    this.messageRoster = 'Liste corrigée : les libellés publics ont été recalculés.';
  }

  /** Tableau de lignes éditables, partagé par l'aperçu d'import et la correction. */
  private renderLignes(
    lignes: readonly { prenom: string; nom: string }[],
    surChamp: (index: number, champ: 'prenom' | 'nom', valeur: string) => void,
    surRetrait: (index: number) => void,
  ) {
    const libelles = assignLabels(lignes);
    return html`
      <div class="lignes">
        ${lignes.map(
          (ligne, index) => html`
            <div class="ligne">
              <input
                type="text"
                class="ligne__champ"
                aria-label="Prénom, ligne ${index + 1}"
                placeholder="Prénom"
                .value=${ligne.prenom}
                @input=${(evenement: Event) => surChamp(index, 'prenom', valeurDe(evenement))}
              />
              <input
                type="text"
                class="ligne__champ"
                aria-label="Nom, ligne ${index + 1}"
                placeholder="Nom"
                .value=${ligne.nom}
                @input=${(evenement: Event) => surChamp(index, 'nom', valeurDe(evenement))}
              />
              <span class="ligne__label t-mono">${libelles[index]?.label ?? ''}</span>
              <md-icon-button
                class="btn-retirer"
                title="Retirer cette ligne"
                aria-label="Retirer ${libelles[index]?.label ?? `la ligne ${index + 1}`}"
                @click=${() => surRetrait(index)}
                >${icone('poubelle', 18)}</md-icon-button
              >
            </div>
          `,
        )}
      </div>
    `;
  }

  /** Importe **la liste corrigée**, pas la détection brute. */
  private importer(): void {
    const personnes = assignLabels(this.rosterApercu.filter((ligne) => ligne.prenom.trim() !== '' || ligne.nom.trim() !== ''));
    if (personnes.length === 0) return;
    void this.store.importRoster(personnes);
    this.rosterText = '';
    this.rosterApercu = [];
    this.rosterDetection = null;
    this.rosterAvertissements = [];
    if (this.nombreCodes === 0) this.nombreCodes = personnes.length + 4;
  }

  private renderImport() {
    const detection = this.rosterDetection;
    const libelles = assignLabels(this.rosterApercu);
    const ordres: readonly { cle: OrdreColonnes; libelle: string }[] = [
      { cle: 'auto', libelle: 'Détecter' },
      { cle: 'prenom_nom', libelle: 'Prénom NOM' },
      { cle: 'nom_prenom', libelle: 'NOM Prénom' },
    ];

    return html`
      <div class="bloc">
        <p class="t-label">Ce qu’il faut faire</p>
        <div class="field">
          <label for="roster">Liste des élèves</label>
          <textarea
            id="roster"
            placeholder="Alice Dupont&#10;Bruno Martin&#10;DUPONT Chloé"
            .value=${this.rosterText}
            @input=${(evenement: Event) => {
              this.rosterText = valeurDe(evenement);
              this.analyserRoster();
            }}
          ></textarea>
        </div>
        <div class="field">
          <label>Ordre des colonnes</label>
          <div class="segmented" role="group" aria-label="Ordre des colonnes">
            ${ordres.map(
              (ordre) => html`
                <button
                  type="button"
                  class="segmented__item segmented__item--large"
                  aria-pressed=${this.rosterOrder === ordre.cle}
                  @click=${() => {
                    this.rosterOrder = ordre.cle;
                    this.analyserRoster();
                  }}
                >
                  <span class="segmented__nom">${ordre.libelle}</span>
                </button>
              `,
            )}
          </div>
        </div>
      </div>

      ${detection === null
        ? nothing
        : html`<div class="bloc">
            <p class="t-label">Ce qui a été reconnu</p>
            <p class="t-body">
              ${pluriel(libelles.length, 'ligne reconnue', 'lignes reconnues')} · séparateur
              ${detection.separator === null
                ? 'aucun (une colonne)'
                : detection.separator === '\t'
                  ? 'tabulation'
                  : `« ${detection.separator} »`}
              · ${detection.hasHeader ? 'ligne d’en-tête ignorée' : 'sans en-tête'}
            </p>
            ${this.rosterAvertissements.map((avertissement) => html`<p class="t-body t-error">${avertissement}</p>`)}
          </div>`}
      ${this.rosterApercu.length === 0
        ? nothing
        : html`<div class="bloc">
            <p class="t-label">Corrigez les lignes si besoin</p>
            ${this.renderLignes(
              this.rosterApercu,
              (index, champ, valeur) => this.majLigne(index, champ, valeur),
              (index) => this.supprimerLigne(index),
            )}
            <div class="row row--between">
              <md-text-button @click=${() => this.ajouterLigne()}>Ajouter une ligne</md-text-button>
              <md-filled-button ?disabled=${this.rosterApercu.length === 0} @click=${() => this.importer()}
                >Importer ${pluriel(this.rosterApercu.length, 'élève', 'élèves')}</md-filled-button
              >
            </div>
          </div>`}
    `;
  }

  private renderRoster() {
    const persons = this.store.state.persons;
    return html`
      <section class="card">
        <div class="card__head">
          <span class="card__step ${persons.length > 0 ? 'card__step--done' : ''}" aria-hidden="true"
            >${persons.length > 0 ? '✓' : '1'}</span
          >
          <div class="stack stack--tight grow">
            <h2 class="t-title">Liste de classe</h2>
            <p class="t-body t-muted">
              Collez la liste, un élève par ligne. Le séparateur et l’en-tête sont détectés seuls ; choisissez l’ordre
              des colonnes, corrigez les lignes, puis importez. Aucun import n’est silencieux.
            </p>
          </div>
        </div>

        ${persons.length === 0 ? this.renderImport() : this.renderListeImportee(persons)}
      </section>
    `;
  }

  /**
   * Liste déjà importée : elle reste **modifiable**. C'est ce qui permet de
   * corriger une faute de frappe ou un prénom et un nom inversés sans tout
   * ressaisir, et sans casser les candidatures déjà déclarées.
   */
  private renderListeImportee(persons: readonly Person[]) {
    if (this.editionRoster) {
      return html`
        <div class="bloc">
          <p class="t-label">Corrigez la liste</p>
          <p class="t-body t-muted">
            Corrigez une faute, échangez un prénom et un nom, retirez une ligne ou ajoutez-en une. Les libellés
            publics sont recalculés à l’enregistrement ; les candidatures déjà déclarées sont conservées.
          </p>
          ${this.renderLignes(
            this.editionLignes,
            (index, champ, valeur) => this.majEdition(index, champ, valeur),
            (index) => this.retirerEdition(index),
          )}
          ${this.messageRoster !== '' ? html`<p class="t-body t-error">${this.messageRoster}</p>` : nothing}
        </div>
        <div class="bloc bloc--actions">
          <p class="t-label">Actions</p>
          <div class="row">
            <md-text-button @click=${() => this.ajouterEdition()}>Ajouter une ligne</md-text-button>
            <md-text-button @click=${() => this.annulerEditionRoster()}>Annuler</md-text-button>
            <md-filled-button @click=${() => void this.enregistrerEditionRoster()}>Enregistrer la liste</md-filled-button>
          </div>
        </div>
      `;
    }
    return html`
      <div class="bloc">
        <p class="t-label">Constat</p>
        <p class="t-body">
          <strong>${pluriel(persons.length, 'élève', 'élèves')}</strong> importés.
          <span class="t-muted"
            >Libellés publics : ${persons.slice(0, 6).map((personne) => personne.label).join(', ')}${persons.length > 6
              ? '…'
              : ''}</span
          >
        </p>
        ${this.messageRoster !== '' ? html`<p class="t-body t-success">${this.messageRoster}</p>` : nothing}
      </div>
      <div class="bloc bloc--actions">
        <p class="t-label">Actions</p>
        <div class="row">
          <md-filled-tonal-button @click=${() => this.commencerEditionRoster()}>Modifier la liste</md-filled-tonal-button>
          <md-text-button @click=${() => void this.store.importRoster([])}>Remplacer la liste</md-text-button>
        </div>
      </div>
    `;
  }

  // ------------------------------------------------------------------- codes

  private async generer(): Promise<void> {
    const nombre = this.nombreCodes > 0 ? this.nombreCodes : this.store.state.persons.length + 4;
    await this.store.generateCodes(nombre);
  }

  /** Adresse à imprimer sur les bulletins : directe dès que l'élection est publiée. */
  private urlPapier(): string {
    return this.store.liaison !== null ? this.urlVoteDirect() : this.urlVote();
  }

  private planche(): void {
    const codes = this.store.codes();
    const config = this.store.state.config;
    if (!codes) return;
    const url = this.urlPapier();
    const corps = `
      <h1>${config?.nom ?? 'Élection'} — ${config?.classe ?? ''}</h1>
      <p class="note">Un code par élève, à découper. Chaque code est à usage unique. En cas de perte, signalez-le
      à l’enseignant : le code ne pourra pas voter deux fois. Pour voter en ligne :
      <strong>${url}</strong> — saisissez le code personnel ci-dessus.</p>
      <div class="teacher">
        <strong>Code enseignant (à conserver, ne pas distribuer)</strong>
        <span class="big">${codes.enseignant.display}</span>
      </div>
      <div class="sheet">
        ${codes.eleves
          .map(
            (code, index) => `<div class="ticket">
              <span class="num">Élève ${index + 1}</span>
              <span class="code">${code.display}</span>
              <span class="hint">${config?.classe ?? ''} — vote : ${url}</span>
            </div>`,
          )
          .join('')}
      </div>`;
    ouvrirImpression(`Codes — ${config?.nom ?? 'élection'}`, corps);
  }

  private renderCodes() {
    const codes = this.store.codes();
    const genere = this.store.hasCodes();
    const nombre = this.nombreCodes > 0 ? this.nombreCodes : this.store.state.persons.length + 4;

    return html`
      <section class="card">
        <div class="card__head">
          <span class="card__step ${genere ? 'card__step--done' : ''}" aria-hidden="true">${genere ? '✓' : '2'}</span>
          <div class="stack stack--tight grow">
            <h2 class="t-title">Codes de vote</h2>
            <p class="t-body t-muted">
              Un code individuel par élève, plus un code enseignant. Les codes ne sont jamais enregistrés en clair
              dans le journal : seules leurs empreintes le sont.
            </p>
          </div>
        </div>

        ${!genere
          ? html`<div class="bloc bloc--actions">
              <p class="t-label">Ce qu’il faut faire</p>
              <div class="field">
                <label for="nb-codes">Nombre de codes élèves</label>
                <input
                  id="nb-codes"
                  type="number"
                  min="1"
                  max="200"
                  .value=${String(nombre)}
                  @input=${(evenement: Event) => (this.nombreCodes = Number(valeurDe(evenement)))}
                />
              </div>
              <md-filled-button @click=${() => void this.generer()}>Générer les codes</md-filled-button>
            </div>`
          : html`
              <div class="bloc">
                <p class="t-label">Constat</p>
                <p class="t-body">
                  <strong>${pluriel(this.store.state.codes?.count ?? 0, 'code élève', 'codes élèves')}</strong> générés
                  et imprimables.
                </p>
              </div>
              <div class="bloc">
                <p class="t-label">Code enseignant — à conserver, ne pas projeter</p>
                ${this.codeEnseignantVisible
                  ? html`<p class="t-display t-mono">${codes?.enseignant.display ?? '—'}</p>`
                  : html`<p class="t-display t-mono masque" aria-label="Code enseignant masqué">•••••-•••••-•••</p>`}
                <p class="t-body t-muted">
                  Il verrouille cet espace et ouvre les décisions souveraines. Il figure aussi en tête de la planche
                  imprimée. <strong>Cet écran ne doit pas être projeté.</strong>
                </p>
                <div class="row">
                  <md-text-button @click=${() => (this.codeEnseignantVisible = !this.codeEnseignantVisible)}
                    >${this.codeEnseignantVisible ? 'Masquer le code' : 'Afficher le code'}</md-text-button
                  >
                </div>
              </div>
              ${codes
                ? html`<div class="bloc bloc--actions">
                    <p class="t-label">Actions</p>
                    <div class="row row--between">
                      <md-outlined-button @click=${() => this.planche()}>Imprimer la planche</md-outlined-button>
                      <md-text-button @click=${() => void this.generer()}>Régénérer</md-text-button>
                    </div>
                    <p class="t-body t-muted">
                      Chaque bulletin porte l’adresse du vote : <span class="t-mono">${this.urlPapier()}</span>.
                    </p>
                  </div>`
                : html`<div class="bloc">
                    <p class="t-body t-error">
                      La planche imprimable n’est plus disponible sur cet appareil. Régénérez les codes si nécessaire.
                    </p>
                  </div>`}
              ${codes
                ? html`<details class="details no-print">
                    <summary class="t-title">
                      Afficher ${pluriel(codes.eleves.length, 'le code élève', `les ${codes.eleves.length} codes élèves`)}
                    </summary>
                    <p class="t-body t-muted">
                      Un élève peut voter sans papier si vous lui dictez son code. Les codes sont conservés sur cet
                      appareil, jamais dans le journal. <strong>Ne projetez pas cette liste.</strong>
                    </p>
                    <div class="sheet">
                      ${codes.eleves.map(
                        (code, index) => html`
                          <div class="ticket">
                            <span class="ticket__num">Élève ${index + 1}</span>
                            <span class="ticket__code">${code.display}</span>
                            <span class="ticket__hint">${this.urlPapier()}</span>
                          </div>
                        `,
                      )}
                    </div>
                  </details>`
                : nothing}
            `}
      </section>
    `;
  }

  // ------------------------------------------------------------ candidatures

  private renderCandidatures() {
    const { computation, state } = this.store;
    const persons = state.persons;
    const binomes = computation.items.filter((item) => item.kind === 'binome');
    const individus = computation.items.filter((item) => item.kind === 'individu');
    const ouvertes = state.phase === 'BROUILLON' || state.phase === 'CANDIDATURES';

    return html`
      <section class="card">
        <div class="card__head">
          <span class="card__step ${binomes.length > 0 ? 'card__step--done' : ''}" aria-hidden="true"
            >${binomes.length > 0 ? '✓' : '3'}</span
          >
          <div class="stack stack--tight grow">
            <h2 class="t-title">Candidatures</h2>
            <p class="t-body t-muted">
              Déclarez les binômes. Toute personne ne figurant sur aucun binôme reste votable seule, sans suppléant
              (mode « liste de classe »).
            </p>
          </div>
        </div>

        ${binomes.length === 0 && !ouvertes
          ? html`<div class="bloc">
              <p class="t-body t-muted">Les candidatures sont closes et aucun binôme n’a été enregistré.</p>
            </div>`
          : nothing}

        ${binomes.length > 0
          ? html`<div class="bloc">
              <p class="t-label">Constat</p>
              <ul class="list">
                ${binomes.map(
                  (item) => html`
                    <li class="list__item">
                      <span class="grow">${item.label} <span class="option__kind">binôme</span></span>
                      ${ouvertes
                        ? html`<md-text-button @click=${() => void this.store.removeItem(item.id)}>Retirer</md-text-button>`
                        : nothing}
                    </li>
                  `,
                )}
              </ul>
            </div>`
          : nothing}

        ${ouvertes && persons.length > 0
          ? html`<div class="bloc bloc--actions">
              <p class="t-label">Ce qu’il faut faire</p>
              <div class="grid-2">
                <div class="field">
                  <label for="titulaire">Candidat titulaire</label>
                  <select
                    id="titulaire"
                    .value=${this.titulaireId}
                    @change=${(evenement: Event) => (this.titulaireId = valeurDe(evenement))}
                  >
                    <option value="">— choisir —</option>
                    ${persons.map(
                      (personne) => html`<option value=${personne.id} ?selected=${personne.id === this.titulaireId}>${personne.label}</option>`,
                    )}
                  </select>
                </div>
                <div class="field">
                  <label for="suppleant">Suppléant déclaré</label>
                  <select
                    id="suppleant"
                    .value=${this.suppleantId}
                    @change=${(evenement: Event) => (this.suppleantId = valeurDe(evenement))}
                  >
                    <option value="">— aucun —</option>
                    ${persons
                      .filter((personne) => personne.id !== this.titulaireId)
                      .map(
                        (personne) => html`<option value=${personne.id} ?selected=${personne.id === this.suppleantId}>${personne.label}</option>`,
                      )}
                  </select>
                </div>
              </div>
              <md-filled-tonal-button
                ?disabled=${this.titulaireId === ''}
                @click=${() => {
                  void this.store.declare(this.titulaireId, this.suppleantId === '' ? null : this.suppleantId);
                  this.titulaireId = '';
                  this.suppleantId = '';
                }}
                >Déclarer le binôme</md-filled-tonal-button
              >
            </div>`
          : nothing}

        ${individus.length > 0
          ? html`<div class="bloc">
              <p class="t-label">Votables seules</p>
              <p class="t-body t-muted">
                ${pluriel(individus.length, 'personne', 'personnes')} hors binôme :
                ${individus.slice(0, 8).map((item) => item.label).join(', ')}${individus.length > 8 ? '…' : ''}
              </p>
            </div>`
          : nothing}
      </section>
    `;
  }

  // -------------------------------------------------------------- déroulement

  private itemsVotables(): number {
    return this.store.computation.items.length;
  }

  private renderDeroulement() {
    const { state, computation } = this.store;
    const phase = state.phase;
    const peutOuvrir = state.persons.length > 0 && this.store.hasCodes();

    const bouton = (libelle: string, cible: Phase, actif: boolean, aide = '') => html`
      <div class="stack stack--tight">
        <md-filled-button ?disabled=${!actif} @click=${() => void this.store.setPhase(cible)}>${libelle}</md-filled-button>
        ${aide ? html`<span class="t-body t-muted">${aide}</span>` : nothing}
      </div>
    `;

    return html`
      <section class="card">
        <div class="card__head">
          <span class="chip-icon" aria-hidden="true">${icone('horloge', 22)}</span>
          <div class="stack stack--tight grow">
            <h2 class="t-title">Déroulement du scrutin</h2>
            <p class="t-body t-muted">
              L’enseignant ouvre et ferme chaque phase. Rien n’est automatique : une phase ne change que sur une
              décision explicite, journalisée.
            </p>
          </div>
          <span class="pill">${PHASE_LABEL[phase]}</span>
        </div>

        <div class="bloc">
          <p class="t-label">Constat</p>
          <p class="t-body t-muted">
            ${pluriel(computation.round1.inscrits, 'inscrit', 'inscrits')} ·
            ${pluriel(computation.round1.votants, 'votant', 'votants')} ·
            ${pourcentage(computation.round1.votants, computation.round1.inscrits)} de participation
          </p>
        </div>

        <div class="bloc bloc--actions">
          <p class="t-label">Action attendue</p>
          ${phase === 'BROUILLON'
            ? bouton('Ouvrir les candidatures', 'CANDIDATURES', peutOuvrir, peutOuvrir ? '' : 'Importez la liste et générez les codes.')
            : nothing}
          ${phase === 'CANDIDATURES' ? bouton('Clore les candidatures', 'VALIDATION', true) : nothing}
          ${phase === 'VALIDATION'
            ? bouton(
                'Ouvrir le premier tour',
                'TOUR1',
                this.itemsVotables() >= 2,
                this.itemsVotables() >= 2 ? '' : 'Il faut au moins deux items votables.',
              )
            : nothing}
          ${phase === 'TOUR1' ? bouton('Clore le scrutin', 'DEPOUILLEMENT1', true) : nothing}
          ${phase === 'TOUR2' ? bouton('Clore le scrutin', 'DEPOUILLEMENT2', true) : nothing}
          ${phase === 'DEPOUILLEMENT1' || phase === 'DEPOUILLEMENT2'
            ? html`
                ${computation.ambigu
                  ? bouton('Aller au départage', 'DEPARTAGE', true)
                  : computation.tour2Requis && phase === 'DEPOUILLEMENT1'
                    ? bouton('Ouvrir le second tour', 'TOUR2', true, `${pluriel(computation.seatsRestants, 'siège restant à pourvoir', 'sièges restants à pourvoir')}.`)
                    : bouton('Proclamer les résultats', 'PROCLAMATION', true)}
              `
            : nothing}
          ${phase === 'DEPARTAGE' ? bouton('Revenir au dépouillement', computation.round2 ? 'DEPOUILLEMENT2' : 'DEPOUILLEMENT1', true) : nothing}
          ${phase === 'ACCEPTATION' || phase === 'DESIGNATION'
            ? bouton('Proclamer les résultats', 'PROCLAMATION', true)
            : nothing}
        </div>

        <div class="bloc bloc--actions">
          <p class="t-label">Vues</p>
          <div class="row">
            <md-text-button @click=${() => (location.hash = `#/affichage/${this.store.electionId ?? ''}`)}
              >Ouvrir l’affichage</md-text-button
            >
            <md-text-button @click=${() => (location.hash = `#/isoloir/${this.store.electionId ?? ''}`)}
              >Ouvrir l’isoloir</md-text-button
            >
          </div>
        </div>
      </section>
    `;
  }

  // ------------------------------------------------------------- dépouillement

  private renderTour(result: (typeof this.store.computation)['round1'] | null, titre: string) {
    if (result === null) return nothing;
    const total = result.valides + result.blancs + result.nuls;
    if (total === 0) return nothing;
    const maximum = Math.max(1, ...result.classement.map((entry) => entry.voix));
    const motifNuls = Object.entries(result.nulsParMotif);

    return html`
      <div class="bloc">
        <div class="row row--between">
          <h3 class="t-title">${titre}</h3>
          <span class="pill">${result.seuil === null ? 'majorité relative' : `majorité absolue à ${result.seuil}`}</span>
        </div>
        ${result.classement.map(
          (entry) => html`
            <div class="result ${result.majoritaires.includes(entry.itemId) ? 'result--majoritaire' : ''}">
              <div class="result__head">
                <span class="result__name">${entry.label}</span>
                <span class="result__voix">${entry.voix}</span>
              </div>
              <div class="result__track">
                <div class="result__fill" style="width: ${Math.round((entry.voix / maximum) * 100)}%"></div>
              </div>
            </div>
          `,
        )}
        <p class="t-body t-muted">
          ${pluriel(result.complets, 'bulletin complet', 'bulletins complets')} ·
          ${pluriel(result.partiels, 'partiel', 'partiels')} · ${pluriel(result.blancs, 'blanc', 'blancs')} ·
          ${pluriel(result.nuls, 'nul', 'nuls')}
        </p>
        ${motifNuls.length > 0
          ? html`<p class="t-body t-muted">
              Motifs des nuls :
              ${motifNuls.map(([motif, nombre]) => `${NUL_REASON_LABEL[motif as keyof typeof NUL_REASON_LABEL] ?? motif} ×${nombre}`).join(', ')}
            </p>`
          : nothing}
      </div>
    `;
  }

  private renderDepouillement() {
    const { state, computation } = this.store;
    const phase = state.phase;
    const apresScrutin = ['DEPOUILLEMENT1', 'TOUR2', 'DEPOUILLEMENT2', 'DEPARTAGE', 'ACCEPTATION', 'DESIGNATION', 'PROCLAMATION'].includes(
      phase,
    );
    if (!apresScrutin) {
      return html`
        <section class="card">
          <div class="card__head">
            <span class="card__step card__step--todo" aria-hidden="true">5</span>
            <div class="stack stack--tight grow">
              <h2 class="t-title">Dépouillement</h2>
              <p class="t-body t-muted">
                Le dépouillement s’ouvre après la clôture du scrutin. Il est calculé à partir du journal, jamais
                saisi à la main.
              </p>
            </div>
          </div>
          <div class="bloc">
            <p class="t-label">Pour débloquer cette étape</p>
            <p class="t-body t-muted">Clôturez le scrutin depuis l’étape 4.</p>
            <md-outlined-button @click=${() => (this.etapeChoisie = 4)}>Aller à l’étape 4 — Scrutin</md-outlined-button>
          </div>
        </section>
      `;
    }

    const retenus = retainedSeats(state, computation);
    const personnes = state.persons;
    const mandatsAttendus = computation.siegesCalcules
      .filter((siege) => siege.role === 'titulaire')
      .filter((siege) => siege.itemId?.startsWith('ind_') ?? false)
      .filter((siege) => !state.mandates.some((mandat) => mandat.personId === siege.personId));

    return html`
      <section class="card">
        <div class="card__head">
          <span class="card__step card__step--done" aria-hidden="true">5</span>
          <div class="stack stack--tight grow">
            <h2 class="t-title">Dépouillement</h2>
            <p class="t-body t-muted">
              Le calcul reste toujours affiché. Une décision souveraine ne l’efface jamais : elle s’y ajoute.
            </p>
          </div>
        </div>

        ${this.renderTour(computation.round1, 'Premier tour')}
        ${this.renderTour(computation.round2, 'Second tour')}

        ${computation.ambigu
          ? html`<div class="bloc">
              <div class="card card--warn">
                <p class="t-title">Égalité — départage obligatoire</p>
                <p class="t-body">
                  Aucune égalité n’est tranchée automatiquement. Choisissez l’item retenu et motivez la décision :
                  elle sera consignée au procès-verbal.
                </p>
                <div class="field">
                  <label for="departage">Item retenu (tour ${computation.ambigu.round})</label>
                  <select
                    id="departage"
                    .value=${this.departageChoisi}
                    @change=${(evenement: Event) => (this.departageChoisi = valeurDe(evenement))}
                  >
                    <option value="">— choisir —</option>
                    ${computation.ambigu.itemIds.map(
                      (itemId) => html`<option value=${itemId}>
                        ${computation.items.find((item) => item.id === itemId)?.label ?? itemId}
                      </option>`,
                    )}
                  </select>
                </div>
                <div class="field">
                  <label for="motif-departage">Motif du départage</label>
                  <input
                    id="motif-departage"
                    type="text"
                    .value=${this.motif}
                    @input=${(evenement: Event) => (this.motif = valeurDe(evenement))}
                  />
                </div>
                <div class="row row--end">
                  <md-filled-button
                    ?disabled=${this.departageChoisi === '' || this.motif.trim() === ''}
                    @click=${() => {
                      const ambigu = computation.ambigu;
                      if (!ambigu) return;
                      void this.store.decideTiebreak(ambigu.round, [...ambigu.itemIds], this.departageChoisi, this.motif.trim());
                      this.departageChoisi = '';
                      this.motif = '';
                    }}
                    >Trancher l’égalité</md-filled-button
                  >
                </div>
              </div>
            </div>`
          : nothing}

        ${mandatsAttendus.length > 0
          ? html`<div class="bloc">
              <p class="t-label">Acceptation du mandat</p>
              <p class="t-body t-muted">
                Ces personnes ont été élues sans s’être portées candidates. Leur acceptation doit être recueillie
                avant la proclamation.
              </p>
              <ul class="list">
                ${mandatsAttendus.map(
                  (siege) => html`
                    <li class="list__item">
                      <span class="grow">${personLabel(state, siege.personId)}</span>
                      <md-text-button
                        @click=${() => void this.store.decideMandate(siege.personId, false, 'refus du mandat')}
                        >Refuse</md-text-button
                      >
                      <md-filled-tonal-button
                        @click=${() => void this.store.decideMandate(siege.personId, true, 'acceptation recueillie en classe')}
                        >Accepte</md-filled-tonal-button
                      >
                    </li>
                  `,
                )}
              </ul>
            </div>`
          : nothing}

        ${computation.designationsAttendues.length > 0
          ? html`<div class="bloc">
              <p class="t-label">Désignation des suppléants</p>
              <p class="t-body t-muted">
                Chaque personne élue seule désigne son suppléant, dans la liste de classe. L’enseignant valide.
              </p>
              ${computation.designationsAttendues.map(
                (attendue) => html`
                  <div class="stack stack--tight">
                    <div class="row row--between">
                      <span class="t-body">${attendue.label} désigne…</span>
                    </div>
                    <div class="row">
                      <select
                        class="grow"
                        @change=${(evenement: Event) => (this.designeId = valeurDe(evenement))}
                        .value=${this.designeId}
                      >
                        <option value="">— choisir —</option>
                        ${personnes
                          .filter((personne) => personne.id !== attendue.titulaireId)
                          .map((personne) => html`<option value=${personne.id}>${personne.label}</option>`)}
                      </select>
                      <md-filled-button
                        ?disabled=${this.designeId === ''}
                        @click=${() => {
                          void this.store.designate(attendue.titulaireId, this.designeId, 'désignation validée par l’enseignant');
                          this.designeId = '';
                        }}
                        >Valider</md-filled-button
                      >
                    </div>
                  </div>
                `,
              )}
            </div>`
          : nothing}

        <div class="bloc">
          <p class="t-label">Sièges retenus</p>
          ${retenus.length === 0
            ? html`<p class="t-body t-muted">Aucun siège pourvu à ce stade.</p>`
            : html`<ul class="list">
                ${retenus.map(
                  (siege: Seat) => html`
                    <li class="list__item">
                      <span class="pill ${siege.via === 'force' ? 'pill--warn' : 'pill--ok'}">${ROLE_LABEL[siege.role] ?? siege.role}</span>
                      <span class="grow">${personLabel(state, siege.personId)}</span>
                      <span class="t-body t-muted">${siege.via === 'force' ? 'décision de l’enseignant' : siege.via === 'designe' ? 'désigné' : 'élu'}</span>
                    </li>
                  `,
                )}
              </ul>`}
        </div>
      </section>
    `;
  }

  // ------------------------------------------------------ décision souveraine

  private cleSiege(role: string, index: number): string {
    return `${role}-${index}`;
  }

  private renderSouverain() {
    const { state, computation } = this.store;
    const retenus = retainedSeats(state, computation);
    const actes = [...state.validations, ...state.overrides].sort((a, b) => a.ts - b.ts);
    const partie = state.events.length > 0;

    return html`
      <section class="card">
        <div class="card__head">
          <span class="chip-icon" aria-hidden="true">${icone('balance', 22)}</span>
          <div class="stack stack--tight grow">
            <h2 class="t-title">Décisions souveraines</h2>
            <p class="t-body t-muted">
              Pour couvrir une situation imprévue. Le résultat calculé reste toujours affiché : une modification ne
              l’efface jamais, elle s’y ajoute.
            </p>
          </div>
        </div>

        <div class="bloc bloc--actions">
          <p class="t-label">Actions</p>
          <div class="row">
            <md-outlined-button
              ?disabled=${!partie}
              @click=${() => {
                void this.store.validateResult(this.motif.trim() || 'validation du résultat calculé');
                this.motif = '';
              }}
              >Valider le résultat calculé</md-outlined-button
            >
            <md-filled-tonal-button ?disabled=${!partie} @click=${() => (this.souverainOuvert = !this.souverainOuvert)}
              >Modifier les résultats</md-filled-tonal-button
            >
          </div>
        </div>

        ${this.souverainOuvert
          ? html`
              <div class="bloc">
                <p class="t-label">Attribution retenue</p>
                ${[0, 1].map((index) => {
                  const siege = retenus.filter((entree) => entree.role === 'titulaire')[index];
                  const cle = this.cleSiege('titulaire', index);
                  const valeur = this.forcer[cle] ?? siege?.personId ?? '';
                  return html`
                    <div class="field">
                      <label for=${cle}>Titulaire ${index + 1}${siege ? ` (calculé : ${personLabel(state, siege.personId)})` : ''}</label>
                      <select
                        id=${cle}
                        .value=${valeur}
                        @change=${(evenement: Event) => (this.forcer = { ...this.forcer, [cle]: valeurDe(evenement) })}
                      >
                        <option value="">— siège vacant —</option>
                        ${state.persons.map(
                          (personne: Person) => html`<option value=${personne.id} ?selected=${personne.id === valeur}>
                            ${personne.label}
                          </option>`,
                        )}
                      </select>
                    </div>
                  `;
                })}
                ${[0, 1].map((index) => {
                  const siege = retenus.filter((entree) => entree.role === 'suppleant')[index];
                  const cle = this.cleSiege('suppleant', index);
                  const valeur = this.forcer[cle] ?? siege?.personId ?? '';
                  return html`
                    <div class="field">
                      <label for=${cle}>Suppléant ${index + 1}${siege ? ` (calculé : ${personLabel(state, siege.personId)})` : ''}</label>
                      <select
                        id=${cle}
                        .value=${valeur}
                        @change=${(evenement: Event) => (this.forcer = { ...this.forcer, [cle]: valeurDe(evenement) })}
                      >
                        <option value="">— siège vacant —</option>
                        ${state.persons.map(
                          (personne: Person) => html`<option value=${personne.id} ?selected=${personne.id === valeur}>
                            ${personne.label}
                          </option>`,
                        )}
                      </select>
                    </div>
                  `;
                })}
                <div class="field">
                  <label for="motif-souverain">Motif, obligatoire, reporté au procès-verbal</label>
                  <input
                    id="motif-souverain"
                    type="text"
                    .value=${this.motif}
                    placeholder="Ex. : retrait d’une candidate le jour du scrutin"
                    @input=${(evenement: Event) => (this.motif = valeurDe(evenement))}
                  />
                </div>
                <label class="row" style="gap: 10px; align-items: center">
                  <md-checkbox
                    ?checked=${this.confirme}
                    @change=${(evenement: Event) =>
                      (this.confirme = (evenement.target as HTMLElement & { checked?: boolean }).checked ?? false)}
                  ></md-checkbox>
                  <span class="t-body">Je consigne cette décision au procès-verbal et j’en assume la responsabilité.</span>
                </label>
                <div class="row row--end">
                  <md-filled-button
                    ?disabled=${this.motif.trim() === '' || !this.confirme}
                    @click=${() => void this.enregistrerSouverain()}
                    >Enregistrer la décision</md-filled-button
                  >
                </div>
              </div>
            `
          : nothing}

        ${actes.length > 0
          ? html`<div class="bloc">
              <p class="t-label">Actes enregistrés</p>
              <ul class="list">
                ${actes.map(
                  (acte) => html`
                    <li class="list__item">
                      <span class="pill ${acte.kind === 'modification' ? 'pill--warn' : 'pill--ok'}">
                        ${acte.kind === 'modification' ? 'Modification' : 'Validation'}
                      </span>
                      <span class="grow">
                        ${acte.motif}
                        <br />
                        <span class="t-body t-muted">${dateHeure(acte.ts)} — empreinte calculée ${acte.computedFingerprint.slice(0, 12)}…</span>
                      </span>
                    </li>
                  `,
                )}
              </ul>
            </div>`
          : nothing}
      </section>
    `;
  }

  private async enregistrerSouverain(): Promise<void> {
    const sieges: Seat[] = [];
    for (const index of [0, 1]) {
      const titulaire = this.forcer[this.cleSiege('titulaire', index)] ?? '';
      if (titulaire !== '') sieges.push({ role: 'titulaire', personId: titulaire, via: 'force' });
    }
    for (const index of [0, 1]) {
      const suppleant = this.forcer[this.cleSiege('suppleant', index)] ?? '';
      if (suppleant !== '') sieges.push({ role: 'suppleant', personId: suppleant, via: 'force' });
    }
    await this.store.overrideResult(sieges, this.motif.trim());
    this.souverainOuvert = false;
    this.motif = '';
    this.confirme = false;
    this.forcer = {};
  }

  // ------------------------------------------------------------ vote en ligne

  /** Adresse à projeter : les élèves y saisissent le code de liaison. */
  private urlVote(): string {
    return `${location.origin}/#/vote`;
  }

  /**
   * Adresse encodée dans le QR code : elle porte déjà l'identifiant de
   * l'élection, donc un simple scan mène directement à la saisie du code
   * personnel. Aucune recopie, aucune faute de frappe.
   */
  private urlVoteDirect(): string {
    return `${location.origin}/#/vote/${this.store.electionId ?? ''}`;
  }

  private async publier(): Promise<void> {
    this.publication = true;
    try {
      const code = await this.store.publierEnLigne();
      this.messageLiaison = `Élection publiée. Le code à projeter est ${code}.`;
      await this.rafraichirPresence();
    } catch (erreur) {
      this.messageLiaison = erreur instanceof Error ? erreur.message : 'Publication impossible.';
    } finally {
      this.publication = false;
    }
  }

  private async copierLien(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.urlVoteDirect());
      this.messageLiaison = 'Adresse copiée.';
    } catch {
      this.messageLiaison = 'Copie impossible : notez l’adresse affichée.';
    }
  }

  /** Affiche à coller au mur : le QR, le code, et la marche à suivre. */
  private imprimerAffiche(): void {
    const liaison = this.store.liaison;
    if (liaison === null) return;
    const config = this.store.state.config;
    ouvrirImpression(
      `Vote en ligne — ${config?.nom ?? 'élection'}`,
      `<h1>${config?.nom ?? 'Élection'} — ${config?.classe ?? ''}</h1>
       <p class="note">Scannez ce code avec l’appareil photo de votre téléphone, puis saisissez le code personnel
       imprimé sur votre papier. Sans appareil photo : ouvrez <strong>${this.urlVote()}</strong> et saisissez le
       code de l’élection affiché ci-dessous.</p>
       <div style="display:flex;gap:32px;align-items:center">
         ${qrSvgTexte(this.urlVoteDirect())}
         <div>
           <div style="font-size:14px;color:#444">Code de l’élection</div>
           <div style="font-family:ui-monospace,Menlo,monospace;font-size:40px;font-weight:700;letter-spacing:0.08em">${liaison.code}</div>
           <div style="font-size:14px;color:#444;margin-top:12px">${this.urlVote()}</div>
         </div>
       </div>`,
    );
  }

  private renderPresence() {
    if (!this.store.estEnLigne) return nothing;
    const computation = this.store.computation;
    const courant = this.store.state.phase === 'TOUR2' && computation.round2 !== null ? computation.round2 : computation.round1;

    return html`
      <section class="card">
        <div class="card__head">
          <span class="chip-icon chip-icon--doux" aria-hidden="true">${icone('personnes', 22)}</span>
          <div class="stack stack--tight grow">
            <h2 class="t-title">Élèves connectés</h2>
            <p class="t-body t-muted">
              Appareils ayant la page de vote ouverte à cet instant. Le relais ne connaît ni nom ni identité : il ne
              compte que des identifiants opaques, gardés en mémoire.
            </p>
          </div>
        </div>
        <div class="bloc">
          <p class="t-label">Constat</p>
          <div class="stats">
            <div class="stat">
              <span class="stat__value">${this.connectes ?? '—'}</span>
              <span class="stat__label">connectés</span>
            </div>
            <div class="stat">
              <span class="stat__value">${courant.votants}</span>
              <span class="stat__label">bulletins reçus</span>
            </div>
            <div class="stat">
              <span class="stat__value">${computation.round1.inscrits}</span>
              <span class="stat__label">inscrits</span>
            </div>
            <div class="stat">
              <span class="stat__value">${pourcentage(courant.votants, computation.round1.inscrits)}</span>
              <span class="stat__label">participation</span>
            </div>
          </div>
          ${this.connectes === null
            ? html`<p class="t-body t-muted">
                Compteur indisponible : le relais n’expose pas encore la présence. Le vote fonctionne normalement.
              </p>`
            : nothing}
        </div>
      </section>
    `;
  }

  /**
   * Publication sur le relais : c'est ce qui permet aux élèves de voter depuis
   * leur propre téléphone. Tant que l'élection n'est pas publiée, le vote se
   * fait sur cet appareil, à tour de rôle.
   */
  private renderEnLigne() {
    const liaison = this.store.liaison;
    const pretACreer = this.store.hasCodes() && this.store.state.persons.length > 0;

    return html`
      <section class="card">
        <div class="card__head">
          <span class="chip-icon" aria-hidden="true">${icone('personnes', 22)}</span>
          <div class="stack stack--tight grow">
            <h2 class="t-title">Vote en ligne</h2>
            <p class="t-body t-muted">
              Facultatif. Publiée, l’élection permet à chaque élève de voter depuis <strong>son propre téléphone</strong>.
              Sans publication, le vote se déroule sur cet appareil, l’un après l’autre.
            </p>
          </div>
        </div>

        ${liaison === null
          ? html`<div class="bloc bloc--actions">
              <p class="t-label">Ce qu’il faut faire</p>
              <p class="t-body t-muted">
                Le journal local reste l’autorité : il est simplement recopié sur le relais, et les bulletins
                remontent tout seuls, y compris après une coupure réseau.
              </p>
              <md-filled-tonal-button
                ?disabled=${this.publication || !pretACreer}
                @click=${() => void this.publier()}
                >${this.publication ? 'Publication…' : 'Publier l’élection en ligne'}</md-filled-tonal-button
              >
              ${!pretACreer
                ? html`<p class="t-body t-muted">Il faut d’abord la liste de classe et les codes de vote.</p>`
                : nothing}
            </div>`
          : html`
              <div class="bloc">
                <p class="t-label">Constat</p>
                <div class="card card--accent" data-carte-en-ligne>
                  <div class="grid-2">
                    <div class="qr-cadre">${qrSvg(this.urlVoteDirect())}</div>
                    <div class="stack stack--tight">
                      <p class="t-label">À scanner, ou code à saisir</p>
                      <p class="t-display t-mono" data-code-liaison>${liaison.code}</p>
                      <p class="t-body">
                        Les élèves <strong>scannent ce QR code</strong> avec l’appareil photo de leur téléphone :
                        l’adresse s’ouvre directement. À défaut, ils ouvrent
                        <span class="t-mono">${this.urlVote()}</span> et saisissent le code de l’élection — 6
                        caractères, pas leur code personnel.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="bloc bloc--actions">
                <p class="t-label">Actions</p>
                <div class="row">
                  <md-outlined-button @click=${() => void this.copierLien()}>Copier l’adresse</md-outlined-button>
                  <md-text-button @click=${() => window.open(this.urlVote(), '_blank')}>Ouvrir la page de vote</md-text-button>
                  <md-text-button @click=${() => this.imprimerAffiche()}>Imprimer l’affiche</md-text-button>
                </div>
              </div>
            `}

        ${this.messageLiaison !== '' ? html`<p class="t-body t-success">${this.messageLiaison}</p>` : nothing}
      </section>
    `;
  }

  // -------------------------------------------------------- procès-verbal

  private exporterJournal(): void {
    telecharger(
      nomFichier(`journal-${this.store.state.config?.nom ?? 'election'}`, 'json'),
      JSON.stringify(this.store.events, null, 2),
    );
  }

  /** Sauvegarde complète : journal + planche de codes, restaurable telle quelle. */
  private sauvegarder(): void {
    const contenu = this.store.exporterSauvegarde();
    telecharger(nomFichier(`sauvegarde-${this.store.state.config?.nom ?? 'election'}`, 'json'), contenu);
    this.message = `Sauvegarde exportée à ${dateHeure(Date.now())}. Conservez-la comme le code enseignant : elle contient les codes de vote.`;
  }

  private async restaurer(evenement: Event): Promise<void> {
    const champ = evenement.target as HTMLInputElement;
    const fichier = champ.files?.[0];
    if (fichier === undefined) return;
    try {
      const texte = await fichier.text();
      const electionId = await this.store.importerSauvegarde(texte);
      this.message = 'Sauvegarde restaurée et vérifiée.';
      location.hash = `#/bureau/${electionId}`;
    } catch (erreur) {
      this.message = erreur instanceof Error ? erreur.message : 'Restauration impossible.';
    }
    champ.value = '';
  }

  /**
   * État de préparation : ce que l'enseignant doit voir au vert avant le jour J.
   * Chaque ligne dit explicitement ce qui manque si ce n'est pas prêt.
   */
  private renderPreparation() {
    const diag = this.store.diagnostic();

    const ligne = (etat: boolean, texte: string) => html`
      <li class="list__item">
        <span class="result__marque ${etat ? '' : 'marque-erreur'}" aria-hidden="true"
          >${etat ? icone('checkCercle', 20) : icone('alerte', 20)}</span
        >
        <span class="grow">${texte}</span>
      </li>
    `;

    return html`
      <section class="card">
        <div class="card__head">
          <span class="card__step ${diag.derniereSauvegarde !== null ? 'card__step--done' : ''}" aria-hidden="true"
            >${diag.derniereSauvegarde !== null ? '✓' : '6'}</span
          >
          <div class="stack stack--tight grow">
            <h2 class="t-title">Sauvegarde et préparation</h2>
            <p class="t-body t-muted">
              La sauvegarde embarque la liste, les codes et le journal. Elle se conserve comme le code enseignant :
              c’est le seul moyen de reprendre sur un autre ordinateur.
            </p>
          </div>
        </div>

        <div class="bloc">
          <p class="t-label">Constat</p>
          <ul class="list">
            ${ligne(
              diag.stockageOk,
              diag.stockageOk
                ? 'Stockage local disponible'
                : 'Stockage local INDISPONIBLE : rien ne serait conservé',
            )}
            ${ligne(
              diag.chaineOk,
              diag.chaineOk
                ? `Journal intègre (${pluriel(diag.evenements, 'événement', 'événements')})`
                : `Journal altéré : ${diag.chaineDetail ?? 'chaîne rompue'}`,
            )}
            ${ligne(diag.codes > 0, pluriel(diag.codes, 'code de vote généré', 'codes de vote générés'))}
            ${ligne(diag.eleves > 0, pluriel(diag.eleves, 'élève dans la liste', 'élèves dans la liste'))}
            ${ligne(
              diag.derniereSauvegarde !== null,
              diag.derniereSauvegarde !== null
                ? `Dernière sauvegarde exportée le ${dateHeure(diag.derniereSauvegarde)}`
                : 'Aucune sauvegarde exportée depuis cet appareil',
            )}
            ${diag.purgeLe !== null
              ? html`<li class="list__item">
                  <span class="result__marque" aria-hidden="true">${icone('horloge', 20)}</span>
                  <span class="grow">Données supprimées après le ${dateLongue(diag.purgeLe)}</span>
                </li>`
              : nothing}
          </ul>
        </div>

        ${this.message !== '' ? html`<p class="t-body t-success">${this.message}</p>` : nothing}

        <div class="bloc bloc--actions">
          <p class="t-label">Actions</p>
          <div class="row">
            <md-filled-tonal-button @click=${() => this.sauvegarder()}>Exporter une sauvegarde</md-filled-tonal-button>
            <md-outlined-button @click=${() => this.fichierSauvegarde?.click()}>Restaurer une sauvegarde</md-outlined-button>
            <input
              type="file"
              accept=".json,application/json"
              class="champ-fichier"
              aria-label="Fichier de sauvegarde à restaurer"
              ${ref(this.refFichier)}
              @change=${(evenement: Event) => void this.restaurer(evenement)}
            />
          </div>
          <md-text-button @click=${() => this.exporterJournal()}>Exporter le journal seul</md-text-button>
          <md-text-button @click=${() => void this.supprimerSession()}>Tout effacer maintenant</md-text-button>
        </div>
      </section>
    `;
  }

  private renderPV() {
    return html`
      <section class="card card--flat">
        <div class="card__head">
          <span class="chip-icon chip-icon--doux" aria-hidden="true">${icone('liste', 22)}</span>
          <div class="stack stack--tight grow">
            <h2 class="t-title">Procès-verbal</h2>
            <p class="t-body t-muted">
              Le PV récapitule les deux tours, les blancs, les nuls et leurs motifs, les sièges calculés, les sièges
              retenus et toutes les décisions souveraines. Il est imprimable et exportable.
            </p>
          </div>
        </div>
        <div class="bloc bloc--actions">
          <p class="t-label">Action</p>
          <md-outlined-button @click=${() => (location.hash = `#/pv/${this.store.electionId ?? ''}`)}
            >Voir le procès-verbal</md-outlined-button
          >
        </div>
      </section>
    `;
  }

  // ------------------------------------------------------------ panneaux
  /*
    Les six panneaux sont rendus en permanence et les inactifs sont masqués.
    Raison : l'impression du navigateur et les vérifications automatiques
    atteignent ainsi toutes les commandes, quel que soit le panneau ouvert.
  */
  private renderPanneaux(active: number) {
    const panneau = (cle: number, contenu: unknown) => html`
      <div class="etape-panneau" ?hidden=${cle !== active}>${contenu}</div>
    `;
    return html`
      ${panneau(1, html`<div class="stack">${this.renderRoster()}</div>`)}
      ${panneau(2, html`<div class="stack">${this.renderCodes()}</div>`)}
      ${panneau(3, html`<div class="stack">${this.renderCandidatures()}</div>`)}
      ${panneau(4, html`<div class="stack">${this.renderEnLigne()} ${this.renderPresence()} ${this.renderDeroulement()}</div>`)}
      ${panneau(5, html`<div class="stack">${this.renderDepouillement()} ${this.renderSouverain()}</div>`)}
      ${panneau(6, html`<div class="stack">${this.renderPreparation()} ${this.renderPV()}</div>`)}
    `;
  }

  // ------------------------------------------------------------------ rendu

  override render() {
    if (!this.store.isUnlocked()) return this.renderVerrou();
    const { state } = this.store;
    const etapes = this.etapes();
    const active = this.etapeActive;
    const courante = etapes.find((etape) => etape.cle === active);

    return html`
      <div class="page">
        <div class="row row--between barre-titre">
          <div class="stack stack--tight">
            <span class="eyebrow">${icone('ecole', 17)} Espace enseignant</span>
            <h1 class="t-headline">${state.config?.nom ?? 'Élection'}</h1>
            <p class="t-body t-muted">
              ${state.config?.classe ?? '—'}${state.config?.etablissement ? ` · ${state.config.etablissement}` : ''} ·
              ${PHASE_LABEL[state.phase]}
            </p>
          </div>
          <div class="row barre-actions no-print">
            <md-icon-button
              title="Quitter le bureau"
              aria-label="Quitter le bureau"
              @click=${() => (location.hash = '#/')}
              >${icone('retour', 22)}</md-icon-button
            >
            <md-filled-tonal-icon-button
              class="btn-supprimer"
              title="Supprimer cette session sur cet appareil"
              aria-label="Supprimer cette session sur cet appareil"
              @click=${() => void this.supprimerSession()}
              >${icone('poubelle', 22)}</md-filled-tonal-icon-button
            >
          </div>
        </div>

        <div class="layout-bureau">
          ${this.renderParcours(etapes)}
          <div class="stack">
            ${this.renderProclamation()} ${this.renderRappelSauvegarde()}

            ${courante?.blocage !== null && courante?.blocage !== undefined && courante.debloque !== null && courante.debloque !== courante.cle
              ? html`<section class="card card--warn no-print">
                  <div class="card__head">
                    <span class="chip-icon chip-icon--alerte" aria-hidden="true">${icone('alerte', 22)}</span>
                    <div class="stack stack--tight grow">
                      <h2 class="t-title">Étape ${courante.cle} — ${courante.titre} : bloquée</h2>
                      <p class="t-body">${courante.blocage}</p>
                    </div>
                  </div>
                  <div class="bloc bloc--actions">
                    <md-filled-tonal-button @click=${() => (this.etapeChoisie = courante.debloque)}
                      >Aller à l’étape ${courante.debloque} pour débloquer</md-filled-tonal-button
                    >
                  </div>
                </section>`
              : nothing}

            ${this.renderPanneaux(active)}
          </div>
        </div>
      </div>
    `;
  }
}
